/* Offer math + "Your Funding Package" email HTML.
   Ported from offer.syncupdates.com (fundingPackageHtml / computeOffer) so the CRM
   sends the identical email; CTA points at skylinecaps.com/stips. */

export type Structure = "Daily" | "Weekly" | "Bi-Weekly" | "Monthly";

export interface PrepayTier {
  factor: number;
  days: number;
}

export interface OfferTerms {
  amount: number;
  factor: number;
  term: number; // number of payments
  structure: Structure;
  prepayMode: "tiers" | "none";
  prepays: PrepayTier[];
  stips: string[];
}

export interface RepSignature {
  name: string;   // "Jonathan Fox"
  email: string;  // jfox@skylinecaps.com
  phone?: string; // digits or formatted
}

const STIPS_URL = "https://www.skylinecaps.com/stips";

/** REG-918: who the offer is branded as (public.offer_sender decides). */
export type OfferBrand = "SKYLINE" | "HILTON";

/** Hilton's navy and the signature the Hilton reps use (the App Out signature). */
const HILTON_NAVY = "#0b1f3a";
const HILTON_OFFICE_PHONE = "(954) 278-8954";

function hiltonSignature(rep: RepSignature | null): string {
  const sigName = rep ? esc(properName(rep.name)) : "Hilton Capital";
  const phone = rep && rep.phone ? fmtPhone(rep.phone) : HILTON_OFFICE_PHONE;
  const digits = phone.replace(/\D/g, "").slice(-10);
  const email = rep ? esc(rep.email) : "";
  const font = "font-family:Inter,-apple-system,BlinkMacSystemFont,&quot;Segoe UI&quot;,Helvetica,Arial,sans-serif";
  const label = 'style="font-size:11px;font-weight:700;color:rgb(90,108,129);letter-spacing:0.6px;padding:2px 10px 2px 0px"';
  return (
    '<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;' + font + ';color:rgb(16,35,60);margin-top:24px"><tbody><tr>' +
    '<td style="padding:0px 18px 0px 0px;border-right:3px solid rgb(11,31,58);vertical-align:top">' +
    '<div style="font-size:18px;font-weight:700;color:rgb(11,31,58);line-height:1.2">' + sigName + "</div>" +
    '<div style="font-size:12px;color:rgb(90,108,129);margin-top:3px;line-height:1.3">Funding Specialist | Hilton Capital</div>' +
    '<div style="margin-top:10px"><span style="display:inline-block;font-size:11px;font-weight:700;color:rgb(6,41,31);background-color:rgb(230,250,243);border:1px solid rgb(0,200,151);border-radius:4px;padding:3px 9px;letter-spacing:0.3px">&#10003;&nbsp; Family owned for over 15 years</span></div>' +
    '</td><td style="padding:0px 0px 0px 18px;vertical-align:top"><table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse"><tbody>' +
    "<tr><td " + label + '>PHONE</td><td style="font-size:13px;padding:2px 0px"><a href="tel:+1' + digits + '" style="color:rgb(16,35,60);text-decoration:none">' + esc(phone) + "</a></td></tr>" +
    (email
      ? "<tr><td " + label + '>EMAIL</td><td style="font-size:13px;padding:2px 0px"><a href="mailto:' + email + '">' + email + "</a></td></tr>"
      : "") +
    "<tr><td " + label + '>WEB</td><td style="font-size:13px;padding:2px 0px"><a href="https://hiltoncaps.com/" style="color:rgb(31,60,136);text-decoration:none">Hiltoncaps.com</a></td></tr>' +
    "</tbody></table></td></tr>" +
    '<tr><td colspan="2" style="padding:14px 0px 0px"><div style="border-top:1px solid rgb(223,231,239);padding-top:8px;font-size:10px;line-height:1.4;color:rgb(170,180,192)">Confidential: intended only for the named recipient. If received in error, please notify the sender and delete.</div></td></tr>' +
    "</tbody></table>"
  );
}

export const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");
export const cents = (n: number) =>
  "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function unitFor(structure: Structure): string {
  return { Daily: "days", Weekly: "weeks", "Bi-Weekly": "bi-weeks", Monthly: "months" }[structure] || "weeks";
}

export function fmtPhone(p?: string): string {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 11 && d.charAt(0) === "1") return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return String(p || "").trim();
}

export function properName(s?: string): string {
  return String(s || "")
    .trim()
    .replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export interface ComputedOffer {
  payback: number;
  payment: number;
  frequencyLabel: string; // "Daily" | "Weekly" | ...
  termStr: string;        // "90 days"
  fundingStr: string;
  discounts: Array<[string, string]>; // [label, payback $]
}

export function computeOffer(t: OfferTerms): ComputedOffer {
  const payback = t.amount * t.factor;
  const payments = t.term || 1;
  const unit = unitFor(t.structure);
  const discounts: Array<[string, string]> =
    t.prepayMode === "tiers"
      ? t.prepays
          .filter((p) => p.factor > 0 && p.days > 0)
          .map((p) => [`Within ${p.days} days`, money(t.amount * p.factor)])
      : [];
  return {
    payback,
    payment: payback / payments,
    frequencyLabel: t.structure === "Bi-Weekly" ? "Bi-weekly" : t.structure,
    termStr: `${t.term} ${unit}`,
    fundingStr: money(t.amount),
    discounts,
  };
}

function stipsSentence(stips: string[]): string {
  const names = stips.map((s) => s.trim()).filter(Boolean);
  // The rep deliberately removed every stip: don't reinstate defaults.
  if (names.length === 0) return "requested documents";
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export function fundingPackageHtml(
  terms: OfferTerms,
  merchantFirstName: string,
  rep: RepSignature | null,
  brand: OfferBrand = "SKYLINE",
): string {
  const o = computeOffer(terms);
  const hilton = brand === "HILTON";
  // REG-918: Hilton reps' offers carry Hilton's navy and no Skyline link or name.
  const navy = hilton ? HILTON_NAVY : "#0b2b60";
  const name = esc(properName(merchantFirstName) || "there");
  const sigName = rep ? esc(properName(rep.name)) : "Skyline Capital";
  const sigPhone = rep && rep.phone ? fmtPhone(rep.phone) : "(866) 960-5916";
  const sigEmail = rep && rep.email ? rep.email : "Info@skylinecaps.com";
  const docs = esc(stipsSentence(terms.stips));
  const rows = o.discounts
    .map(
      (d) =>
        "<tr><td style='padding:3px 24px 3px 0;'>" +
        d[0] +
        "</td><td style='font-weight:600;color:#0a1924;'>" +
        d[1] +
        "</td></tr>"
    )
    .join("");
  const discBlock = o.discounts.length
    ? "<p style='color:#6f7f8b;font-size:14px;margin:22px 0 6px;'>Early payment discounts:</p><table style='font-size:14px;color:#6f7f8b;border-collapse:collapse;'>" +
      rows +
      "</table>"
    : "";
  return (
    '<div style="font-family:Arial,Helvetica,sans-serif;max-width:640px;margin:0 auto;font-size:15px;line-height:1.6;color:#1a1a1a;">' +
    (hilton
      ? '<div style="background:linear-gradient(to right,' + navy + ',#16325a);color:#fff;padding:20px 32px;font-size:20px;font-weight:bold;border-radius:8px 8px 0 0;">Your Business Funding Offer Details<div style="color:#c9f2e6;font-size:12px;font-weight:normal;margin-top:4px;">Hilton Capital: fast, flexible, and transparent funding.</div></div>'
      : '<div style="background:linear-gradient(to right,' + navy + ',#09152e);color:#f9b51b;padding:20px 32px;font-size:20px;font-weight:bold;border-radius:8px 8px 0 0;">Your Business Funding Offer Details<div style="color:#fff;font-size:12px;font-weight:normal;margin-top:4px;">Tailored solutions — fast, flexible, and transparent.</div></div>') +
    '<div style="padding:24px 32px;border:1px solid #e6e8ec;border-top:none;border-radius:0 0 8px 8px;">' +
    '<p style="margin-top:0;">Hi <b>' +
    name +
    "</b>, I&#39;m excited to let you know your business has been <strong>approved for a flexible funding offer</strong>, designed to support your current needs and strengthen your long-term growth.</p>" +
    "<p>Below are your offer details — I&#39;ll be standing by to finalize as soon as we confirm your acceptance:</p>" +
    '<table style="border-collapse:collapse;margin:18px 0;width:100%;"><tr>' +
    '<th style="background:' + navy + ';color:#fff;padding:10px;font-size:14px;text-align:left;">Approval Amount</th>' +
    '<th style="background:' + navy + ';color:#fff;padding:10px;font-size:14px;text-align:left;">Term Length</th>' +
    '<th style="background:' + navy + ';color:#fff;padding:10px;font-size:14px;text-align:left;">' +
    o.frequencyLabel +
    " Payment</th></tr><tr>" +
    '<td style="border:1px solid #ccc;padding:10px;font-size:14px;"><em>' +
    o.fundingStr +
    "</em></td>" +
    '<td style="border:1px solid #ccc;padding:10px;font-size:14px;"><em>' +
    o.termStr +
    "</em></td>" +
    '<td style="border:1px solid #ccc;padding:10px;font-size:14px;"><em>' +
    // Whole dollars, matching the portal's fundingPackageHtml exactly.
    money(o.payment) +
    "</em></td></tr></table>" +
    // Hilton has no documents page (hiltoncaps.com/upload takes bank statements
    // only), so a Hilton offer is accepted by replying to the rep, as the
    // Hilton reps' own offers ask.
    (hilton
      ? '<div style="text-align:center;margin:26px 0 8px;"><a href="mailto:' +
        (rep ? esc(rep.email) : "") +
        '?subject=Accepting%20my%20funding%20offer" style="display:inline-block;background:' + navy + ';color:#fff;text-decoration:none;font-weight:bold;font-size:16px;padding:15px 38px;border-radius:10px;">Reply to Accept Your Offer &rarr;</a><div style="font-size:13px;color:#6f7f8b;margin-top:10px;">Reply to this email to accept your offer and send your documents.</div></div>'
      : '<div style="text-align:center;margin:26px 0 8px;"><a href="' +
        STIPS_URL +
        '" style="display:inline-block;background:' + navy + ';color:#fff;text-decoration:none;font-weight:bold;font-size:16px;padding:15px 38px;border-radius:10px;">View &amp; Accept Your Offer &rarr;</a><div style="font-size:13px;color:#6f7f8b;margin-top:10px;">Click above and enter your email to upload your documents and accept your offer.</div></div>') +
    discBlock +
    '<p style="color:' + navy + ';font-weight:bold;font-size:15px;margin-top:22px;">Why This Is a Smart Move for Your Business:</p>' +
    '<ul style="padding-left:20px;margin-top:8px;">' +
    '<li style="margin-bottom:6px;"><strong>Same-Day Ready Capital:</strong> Access funds immediately upon final approval.</li>' +
    '<li style="margin-bottom:6px;"><strong>Refinance Eligibility:</strong> Qualify for refinancing once 50% is paid down.</li>' +
    '<li style="margin-bottom:6px;"><strong style="color:' + navy + ';">No Prepayment Penalties:</strong> Pay off early and save with available discounts.</li>' +
    '<li style="margin-bottom:6px;"><strong>Additional Capital Access:</strong> Receive additional funding with a solid payment history.</li></ul>' +
    (hilton
      ? "<p>To finalize, just reply to this email with your " + docs + " and we&#39;ll take care of the rest.</p>"
      : "<p>To finalize, just <a href=\"" +
        STIPS_URL +
        '" style="color:' + navy + ';font-weight:bold;">open your documents page</a>, enter your email, and upload your ' +
        docs +
        " — we&#39;ll take care of the rest.</p>") +
    '<p style="margin-bottom:0;">Thanks!</p>' +
    (hilton ? hiltonSignature(rep) + "</div></div>" : skylineSignature(rep, navy, sigName, sigPhone, sigEmail))
  );
}

function skylineSignature(rep: RepSignature | null, navy: string, sigName: string, sigPhone: string, sigEmail: string): string {
  return (
    '<div style="margin-top:24px;padding-top:16px;border-top:2px solid ' + navy + ';">' +
    (rep
      ? '<div style="font-weight:bold;color:' + navy + ';font-size:16px;">' +
        sigName +
        '</div><div style="color:#3a3f47;font-size:13.5px;margin-top:2px;">Skyline Capital</div>'
      : '<div style="font-weight:bold;color:' + navy + ';font-size:16px;">Skyline Capital</div>') +
    '<div style="color:#3a3f47;font-size:13.5px;margin-top:4px;">' +
    sigPhone +
    "</div>" +
    '<div style="font-size:13.5px;margin-top:2px;"><a href="mailto:' +
    sigEmail +
    '" style="color:' + navy + ';text-decoration:none;">' +
    sigEmail +
    "</a></div>" +
    "</div></div></div>"
  );
}

/**
 * Send a rep-authored merchant email through the CRM's own pipeline — the
 * send-rep-email edge function authenticates the caller's session, derives
 * the From address from the verified CRM identity (a rep can only send as
 * themselves), enforces rate limits, and queues core_crm.email_send. No
 * external services involved.
 */
/** public.offer_sender: SKYLINE for everyone but the Hilton App Out reps (REG-918). */
export interface OfferSender {
  brand: OfferBrand;
  from_email?: string;
  from_name?: string;
  phone?: string;
  to?: string;
  sandbox?: boolean;
  delivered_to?: string;
  blocked?: string | null;
}

export const OFFER_BLOCKED: Record<string, string> = {
  no_address: 'This merchant has no email address.',
  internal: "That is a company or rep address, not the merchant's.",
  test_address: 'That is a test address.',
  dnd: 'This merchant is Do Not Disturb.',
  no_sandbox_recipient: 'The sandbox recipient is not configured.',
};

/** Who this offer goes out as, for the picked address (public.offer_sender, REG-918). */
export async function fetchOfferSender(merchantId: number, toEmail: string | null): Promise<OfferSender> {
  const { supabaseClient } = await import('../../utils/supabase');
  // offer_sender is not in the generated types yet; same pattern as appout.ts.
  const client = supabaseClient as unknown as {
    rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
  };
  const { data, error } = await client.rpc('offer_sender', { p_merchant: merchantId, p_to_email: toEmail });
  if (error) throw new Error(error.message || 'Could not check who this offer goes out as');
  return (data as OfferSender | null) ?? { brand: 'SKYLINE' };
}

export async function sendFundingPackage(opts: {
  merchantId: number;
  merchantEmail: string;
  html: string;
  /** Defaults to the offer-package subject. */
  subject?: string;
  /** Extra internal CC recipients; the server only accepts @skylinecaps.com (REG-675). */
  cc?: string[];
  /** Extra internal recipients; the server only accepts @skylinecaps.com. */
  bcc?: string[];
  /** Bcc-only mode: server moves the admin copy to Bcc and skips the CC. */
  suppressCc?: boolean;
  /** Sender visible in CC (default) or only Bcc'd; the sender always gets a copy. */
  senderInCc?: boolean;
  /** "offer" = the funding package: Hilton reps send it from their Hilton mailbox (REG-918). */
  kind?: 'offer';
}): Promise<{ ok: boolean; error?: string; brand?: OfferBrand; from?: string; to?: string; cc?: string[]; bcc?: string[] }> {
  const { supabaseClient } = await import('../../utils/supabase');
  const { data, error } = await supabaseClient.functions.invoke('send-rep-email', {
    body: {
      merchantId: opts.merchantId,
      toEmail: opts.merchantEmail,
      subject: opts.subject ?? 'Your Funding Package',
      html: opts.html,
      cc: opts.cc,
      bcc: opts.bcc,
      suppressCc: opts.suppressCc,
      senderInCc: opts.senderInCc,
      kind: opts.kind,
    },
  });
  if (error) {
    // supabase-js wraps non-2xx responses; surface the server's message.
    const context = (error as { context?: Response }).context;
    if (context) {
      const payload = await context.json().catch(() => null);
      if (payload?.error) return { ok: false, error: payload.error };
    }
    return { ok: false, error: error.message ?? 'The email service rejected the send' };
  }
  return data ?? { ok: false, error: 'Empty response from the email service' };
}
